import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { AccountCore, DomainDetail } from '@dev/ee-api/ee-api-models';

import i18n from '@/../i18n';
import {
    addDomain,
    deleteSingleDomain,
    loadDomain,
    loadSingleDomain,
    setAsDefaultSender,
    updateDomain,
    verifyDomain,
} from '@/services/ElasticApi';
import {
    AddDomainFinish,
    AddDomainError,
    DeleteSpecificDomainSuccess,
    DomainSetDefaultSenderSuccess,
    DomainSetDefaultSenderError,
    LoadDomainsError,
    LoadDomainsSuccess,
    LoadSpecificDomainSuccess,
    UpdateDomainError,
    UpdateDomainSuccess,
    VerifySpecificDomainError,
    VerifySpecificDomainSuccess,
} from '@/store/domain/actions';
import { ShowToast } from '@/store/toast/actions';
import {
    DomainAddStartAction,
    DomainDeleteSpecificStartAction,
    DomainFetchSpecificStartAction,
    DomainSetDefaultAction,
    DomainUpdateStartAction,
    DomainVerifySpecificStartAction,
} from '@/store/domain/types';
import { ApplicationState as State } from '@/store/index';
import { StorageKeysEnum } from '@/services/dictionaries/StorageKeys';
import { sendEventToGTM } from '@dev/ee-tools';
import { Logger } from '@/services/errors/appLogger';

const getUserDomains = (state: State): DomainDetail[] => state.domain.domains;

const trimDomainName = (name: string): string => name.split('(')[0].trim();

function* fetchDomain(): any {
    try {
        const domain = yield call(loadDomain);
        yield put(LoadDomainsSuccess(domain?.data));
    } catch (e) {
        Logger.error(e?.response?.data?.Error);
        yield put(LoadDomainsError());
        yield put(
            ShowToast({
                message: `Error: ${e?.response?.data?.Error} `,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}

function* loadSpecificDomain(action: DomainFetchSpecificStartAction): any {
    const {
        payload: { domainName },
    } = action;
    try {
        const domain = yield call(loadSingleDomain, trimDomainName(domainName));
        yield put(LoadSpecificDomainSuccess(domain.data));
    } catch (e) {
        Logger.error(e?.response?.data?.Error);
        yield put(LoadDomainsError());
        yield put(
            ShowToast({
                message: `Error: ${e?.response?.data?.Error} `,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}

function* addNewDomain(action: DomainAddStartAction): any {
    const { payload, meta } = action;
    const domains = yield select(getUserDomains);

    try {
        const addedDomain = yield call(addDomain, payload.newDomain);

        yield put(UpdateDomainSuccess([...domains, ...[addedDomain?.data]]));
        yield put(
            ShowToast({
                message: i18n.t('domainadded'),
                severity: 'info',
                duration: 3000,
            })
        );
        yield put(AddDomainFinish(addedDomain.data, meta));
    } catch (e) {
        const message = `${e?.response?.data?.Error || e?.message}`;
        yield put(
            AddDomainError(
                { message, messageId: e?.response?.data?.MessageID, messageParts: e?.response?.data?.MessageParts },
                meta
            )
        );
        Logger.error(`Error: ${message}`);
    }
}
function* verifySpecificDomain(action: DomainVerifySpecificStartAction): any {
    const { payload, meta } = action;

    try {
        const verifiedDomain = yield call(verifyDomain, trimDomainName(payload.domainName));
        const currentGeneralInfo = yield select((state): AccountCore => state?.account?.GeneralInfo);
        yield put(VerifySpecificDomainSuccess({ ...verifiedDomain.data }, meta));
        if (verifiedDomain?.data?.Spf) {
            sendEventToGTM('DomainVerified', { productType: currentGeneralInfo?.ProductType });
        }
    } catch (e) {
        yield put(VerifySpecificDomainError(`Error: ${e.response?.data?.Error || e.message} `, meta));
        yield put(
            ShowToast({
                message: e.response?.data?.Error || e.message,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}
function* deleteDomain(action: DomainDeleteSpecificStartAction): any {
    const {
        payload: { domainName },
    } = action;
    const domains: DomainDetail[] = yield select(getUserDomains);
    try {
        const name = trimDomainName(domainName);
        yield call(deleteSingleDomain, name); //eslint-disable-line
        yield put(DeleteSpecificDomainSuccess());

        const { IsRewriteDomainValid: isDeletedDomainWithSSL } = domains.find((el) => el.Domain === domainName) || {};

        if (isDeletedDomainWithSSL) {
            localStorage.setItem(StorageKeysEnum.SSLDomainDeletion, domainName);
            yield put(
                ShowToast({
                    message: i18n.t('weareworkingonDeletingyourdomain', { domainName: name }),
                    severity: 'info',
                    duration: 3000,
                })
            );
            const updatedDomains = domains.map((el) => {
                if (el.Domain === domainName) {
                    return { ...el, IsMarkedForDeletion: true };
                }
                return el;
            });
            yield put(UpdateDomainSuccess(updatedDomains));
            return;
        }

        const updatedDomains = domains.filter((el): boolean => el.Domain !== domainName);
        yield put(UpdateDomainSuccess(updatedDomains));

        yield put(
            ShowToast({
                message: i18n.t('domaindeleted', { domainName: name }),
                severity: 'info',
                duration: 3000,
            })
        );
    } catch (e) {
        Logger.error(e?.response?.data?.Error);
        yield put(LoadDomainsError());
        yield put(
            ShowToast({
                message: `Error: ${e?.response?.data?.Error} `,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}

function* updateSpecificDomain(action: DomainUpdateStartAction): any {
    const { payload, meta } = action;
    const domains = yield select(getUserDomains);
    const { VERP, CustomBouncesDomain, IsCustomBouncesDomainDefault, CertificateStatus } = domains.filter(
        (singleDomain): boolean => singleDomain.Domain === payload.domainName
    )[0];
    const trimmedDomainName = trimDomainName(payload.domainName);
    const updatedDomainValues = {
        ...{ VERP, CustomBouncesDomain, IsCustomBouncesDomainDefault, CertificateStatus },
        ...payload.updatedOption,
    };
    try {
        const updateCall = yield call(updateDomain, trimmedDomainName, updatedDomainValues);

        const updatedDomains = domains.map((el): DomainDetail => {
            if (el.Domain === payload.domainName) {
                return { ...updateCall.data, Domain: payload.domainName };
            }
            return el;
        });
        yield put(
            ShowToast({
                message: i18n.t('yourdatahasbeenupdated'),
                severity: 'info',
                duration: 3000,
            })
        );
        yield put(UpdateDomainSuccess(updatedDomains, meta));
    } catch (e) {
        Logger.error(e?.response?.data?.Error);
        yield put(UpdateDomainError(e?.response?.data?.Error, meta));
    }
}

function* setDefaultSenderForDomain(action: DomainSetDefaultAction): any {
    const {
        payload: { email },
        meta,
    } = action;
    try {
        yield call(setAsDefaultSender, email);
        const domain = yield call(loadDomain);
        yield put(LoadDomainsSuccess(domain?.data));
        yield put(
            ShowToast({
                message: i18n.t('defaultsenderwasset'),
                severity: 'info',
                duration: 3000,
            })
        );
        yield put(DomainSetDefaultSenderSuccess(meta));
    } catch (e) {
        Logger.error(e?.response?.data?.Error);
        yield put(
            ShowToast({
                message: `Error: ${e?.response?.data?.Error} `,
                severity: 'error',
                duration: 3000,
            })
        );
        yield put(DomainSetDefaultSenderError(meta));
    }
}

function* watchFetchDomain(): any {
    yield takeEvery('@@domain/DOMAIN_FETCH_START', fetchDomain);
}
function* watchForAddDomain(): any {
    yield takeEvery('@@domain/DOMAIN_ADD_START', addNewDomain);
}
function* watchForLoadSpecificDomain(): any {
    yield takeEvery('@@domain/DOMAIN_FETCH_SINGLE', loadSpecificDomain);
}
function* wachForVerifyDomain(): any {
    yield takeEvery('@@domain/DOMAIN_VERIFY_START', verifySpecificDomain);
}
function* wachForDeleteDomain(): any {
    yield takeEvery('@@domain/DOMAIN_DELETE_START', deleteDomain);
}
function* wachForSetDefault(): any {
    yield takeEvery('@@domain/DOMAIN_SET_DEFAULT', setDefaultSenderForDomain);
}
function* watchForUpdateDomain(): any {
    yield takeEvery('@@domain/DOMAIN_UPDATE_START', updateSpecificDomain);
}
const DomainSagas = [
    fork(watchFetchDomain),
    fork(watchForAddDomain),
    fork(watchForLoadSpecificDomain),
    fork(wachForVerifyDomain),
    fork(wachForDeleteDomain),
    fork(wachForSetDefault),
    fork(watchForUpdateDomain),
];

export default DomainSagas;
